import { useId, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, FileText } from "lucide-react";
import { linkifyText } from "@/lib/linkify";
import { cn } from "@/lib/utils";

interface TicketDescriptionCardProps {
  description?: string | null;
  className?: string;
}

/**
 * Descrição do ticket (texto livre de quem abriu o chamado).
 * - Recolhida em 2 linhas; "Ver mais" só aparece se o texto realmente passa disso.
 * - Expandida, tem altura máxima com rolagem interna, então não empurra o chat.
 * - Preserva quebras de linha e transforma URLs em links clicáveis.
 * - Não renderiza nada quando a descrição é vazia ou ausente.
 */
export function TicketDescriptionCard({ description, className }: TicketDescriptionCardProps) {
  const text = typeof description === "string" ? description.trim() : "";
  const contentId = useId();
  const contentRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  // Descrição nova (ex.: edição ou outro ticket) volta ao estado recolhido.
  useLayoutEffect(() => {
    setExpanded(false);
  }, [text]);

  // Mede se o texto estourou o limite de linhas; reage a mudanças de largura.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || expanded) return;
    const measure = () => setCanExpand(el.scrollHeight - el.clientHeight > 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, expanded]);

  if (!text) return null;

  return (
    <section
      aria-label="Descrição do ticket"
      className={cn("shrink-0 rounded-lg border bg-muted/40 px-4 py-3", className)}
    >
      <div className="flex items-start gap-3">
        <FileText aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Descrição
          </div>
          <p
            id={contentId}
            ref={contentRef}
            className={cn(
              "whitespace-pre-wrap break-words text-sm text-foreground/90",
              expanded ? "max-h-48 overflow-y-auto pr-1" : "line-clamp-2"
            )}
          >
            {linkifyText(text)}
          </p>
          {(canExpand || expanded) && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={contentId}
              className="mt-1.5 inline-flex items-center gap-1 rounded-sm text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {expanded ? "Ver menos" : "Ver mais"}
              <ChevronDown
                aria-hidden="true"
                className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")}
              />
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
